import Autocomplete from '@embracesbs/component-autocomplete/Autocomplete'
import Button2 from '@embracesbs/component-button2/Button2'
import Container from '@embracesbs/component-container/Container'
import Divider from '@embracesbs/component-divider'
import Header from '@embracesbs/component-header/Header'
import Icon from '@embracesbs/component-icon/Icon'
import Switch from '@embracesbs/component-switch'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Toast, { customToast } from '@embracesbs/component-toast'
import React, { useEffect, useState } from 'react'
import { useApi, useAuth } from '../../../Hooks'
import { useTenants } from '../../Context/TenantContext'
import { embracecloudTld } from '../../../Utilities/embracecloudTld'

interface CMSAccordionContentProps {}

const isValidIpAddress = (ip: string): boolean => {
  // Reject CIDR / wildcard immediately (provider uses IPAddress.TryParse — no subnet notation)
  if (ip.includes('/') || ip.includes('*')) return false
  // IPv4: four octets each 0-255
  const ipv4Regex = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/
  if (ipv4Regex.test(ip)) return true
  // IPv6: simplified pattern covering common forms (loopback, full, compressed)
  const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$|^::1$|^::$/
  return ipv6Regex.test(ip)
}

// The provider appends '/redirect?...' to this value, so a trailing slash would double up.
const normalizeBaseUrl = (url: string): string => url.trim().replace(/\/+$/, '')

// The stored shape of the whitelist: trimmed entries, no blanks. Used both for what gets written
// and for comparing an edited list against the stored one.
const normalizeIpList = (csv: string): string[] =>
  csv
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)

// Keys the tenant Terraform creates with their default value. Saving one before that run has
// created it would create the key here first and fail the run, so these are read and written with
// requireExisting: the read 404s while the key is missing, and the API refuses such a save (409).
const REQUIRE_EXISTING = '?requireExisting=true'
const NOT_PROVISIONED_NOTE = 'Created by the tenant deployment and not there yet. Run Update Tenant and save once it has finished.'

const isNotProvisioned = (error: any): boolean => error?.status === 404

// The API explains a refused save (not provisioned yet, or changed meanwhile) in the 409 body.
const saveErrorMessage = (error: any, fallback: string): string =>
  error?.status === 409 && typeof error.data === 'string' && error.data ? error.data : fallback

// index.css sets no body font, so raw elements fall back to serif; use the app font.
const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const CMSAccordionContent: React.FC<CMSAccordionContentProps> = () => {
  const [selectedConversationPacks, setSelectedConversationPacks] = useState<{ label: string; value: string }[]>([])
  const [conversationPackOptions, setConversationPackOptions] = useState<{ label: string; value: string }[]>([])
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('')
  const [turnstileSecretKey, setTurnstileSecretKey] = useState<string>('')
  const [smtpSenderName, setSmtpSenderName] = useState<string>('Embrace - The Human Cloud <no-reply@mail.embracecloud.nl>')
  const [rssEnabled, setRssEnabled] = useState<boolean>(false)
  const [rssIpWhitelist, setRssIpWhitelist] = useState<string>('')
  const [rssRedirectBaseUrl, setRssRedirectBaseUrl] = useState<string>('')
  // What app configuration actually holds. The switch and the whitelist field hold edits that only
  // take effect on Save, so the section needs both to tell a pending change from a live one.
  const [savedRssEnabled, setSavedRssEnabled] = useState<boolean>(false)
  const [savedRssIpWhitelist, setSavedRssIpWhitelist] = useState<string>('')
  // Whether Terraform has created the key yet; null while that is still being read.
  const [packsProvisioned, setPacksProvisioned] = useState<boolean | null>(null)
  const [smtpProvisioned, setSmtpProvisioned] = useState<boolean | null>(null)
  const [rssEnabledProvisioned, setRssEnabledProvisioned] = useState<boolean | null>(null)
  const [rssWhitelistProvisioned, setRssWhitelistProvisioned] = useState<boolean | null>(null)
  const { selectedTenantId, selectedTenant } = useTenants()
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()

  useEffect(() => {
    if (!isLoggedIn || selectedTenantId === null) return
    fetchConversationPackOptions()
    fetchTurnstileSettings()
    fetchSmtpSettings()
    fetchRssSettings()
  }, [isLoggedIn])

  useEffect(() => {
    if (!isLoggedIn || selectedTenantId === null) return
    fetchConversationPacks()
  }, [conversationPackOptions])

  const fetchTurnstileSettings = async () => {
    try {
      const siteKeyResponse = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ConversationsSettings:Tenants:${selectedTenant.tenantName}:Turnstile:SiteKey/conversations`,
        method: 'GET'
      })
      if (siteKeyResponse) {
        setTurnstileSiteKey(siteKeyResponse)
      }
    } catch (error) {
      console.error('Failed to fetch Turnstile SiteKey:', error)
    }

    try {
      const secretKeyResponse = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ConversationsSettings:Tenants:${selectedTenant.tenantName}:Turnstile:SecretKey/conversations`,
        method: 'GET'
      })
      if (secretKeyResponse) {
        setTurnstileSecretKey(secretKeyResponse)
      }
    } catch (error) {
      console.error('Failed to fetch Turnstile SecretKey:', error)
    }
  }

  const fetchSmtpSettings = async () => {
    try {
      const senderResponse = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ConversationsSettings:Tenants:${selectedTenant.tenantName}:SmtpSettings:DefaultSender/conversations${REQUIRE_EXISTING}`,
        method: 'GET'
      })
      setSmtpProvisioned(true)
      if (senderResponse) {
        setSmtpSenderName(senderResponse)
      }
    } catch (error) {
      if (isNotProvisioned(error)) setSmtpProvisioned(false)
      else console.error('Failed to fetch SMTP Sender Name:', error)
    }
  }

  const fetchConversationPackOptions = async () => {
    try {
      const response = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/Settings:conversationPacks/TenantsApp`,
        method: 'GET'
      })
      if (response) {
        setConversationPackOptions(JSON.parse(response))
      }
    } catch (error) {
      alert(error)
      console.error(error)
    }
  }

  const fetchConversationPacks = async () => {
    try {
      const response = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ConversationsSettings:Tenants:${selectedTenant.tenantName}:Packs/conversations${REQUIRE_EXISTING}`,
        method: 'GET'
      })
      setPacksProvisioned(true)
      if (response) {
        setSelectedConversationPacks(conversationPackOptions.filter((option) => response.includes(option.value)))
      }
    } catch (error) {
      if (isNotProvisioned(error)) {
        setPacksProvisioned(false)
        return
      }
      alert(error)
      console.error(error)
    }
  }

  const fetchSuiteBaseUrl = async (): Promise<string> => {
    try {
      const config = await fetchApi({
        endpoint: `/api/tenant/${selectedTenantId}/tenantsconfiguration`,
        method: 'GET'
      })
      const hostname = normalizeBaseUrl(config?.feHostname ?? '')
      if (!hostname) return ''
      return /^https?:\/\//i.test(hostname) ? hostname : `https://${hostname}`
    } catch (error) {
      console.error('Failed to fetch the Suite hostname:', error)
      return ''
    }
  }

  const fetchRssSettings = async () => {
    try {
      const enabledResponse = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ContentProviderSettings:Tenants:${selectedTenant.tenantName}:RssSettings:Enabled/cmscontentprovider${REQUIRE_EXISTING}`,
        method: 'GET'
      })
      setRssEnabledProvisioned(true)
      setRssEnabled(enabledResponse === 'true')
      setSavedRssEnabled(enabledResponse === 'true')
    } catch (error) {
      if (isNotProvisioned(error)) setRssEnabledProvisioned(false)
      else console.error('Failed to fetch RSS Enabled setting:', error)
    }

    try {
      const whitelistResponse = await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}/ContentProviderSettings:Tenants:${selectedTenant.tenantName}:RssSettings:IpWhitelistCsv/cmscontentprovider${REQUIRE_EXISTING}`,
        method: 'GET'
      })
      setRssWhitelistProvisioned(true)
      setRssIpWhitelist(whitelistResponse ?? '')
      setSavedRssIpWhitelist(whitelistResponse ?? '')
    } catch (error) {
      if (isNotProvisioned(error)) setRssWhitelistProvisioned(false)
      else console.error('Failed to fetch RSS IP whitelist:', error)
    }

    setRssRedirectBaseUrl(await fetchSuiteBaseUrl())
  }

  const headerStyle = {
    fontFamily: 'F-Default',
    fontWeight: 'bold',
    width: '100%',
    // The design-system Header defaults to an 80px height with its text
    // flex-centred, plus ~20px/24px padding — collapse both so the title hugs
    // its text at the top-left of the card, aligned with the content below.
    height: 'auto',
    padding: '0 0 12px 0',
    margin: 0
  }

  const sectionContainerStyle = {
    marginBottom: '25px',
    padding: '20px',
    background: 'var(--tma-panel-bg)',
    border: '1px solid var(--tma-list-card-border)',
    borderRadius: '12px'
  }
  const innerContainerStyle = {
    marginBottom: '0px'
  }

  const handleConversationPackChoice = async () => {
    if (selectedConversationPacks.length === 0) {
      customToast.warning("Can't set no conversation packs for tenant")
      return
    }
    const csvPacks = selectedConversationPacks.map((pack: any) => pack.value).join(', ')
    try {
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ConversationsSettings:Tenants:${selectedTenant.tenantName}:Packs`,
          value: csvPacks,
          label: 'conversations',
          requireExisting: true
        }
      })
      customToast.success('Conversation pack saved')
    } catch (error) {
      customToast.error(saveErrorMessage(error, 'Failed to save conversation packs'))
      console.error(error)
    }
  }

  const handleTurnstileSiteKeySave = async () => {
    try {
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ConversationsSettings:Tenants:${selectedTenant.tenantName}:Turnstile:SiteKey`,
          value: turnstileSiteKey,
          label: 'conversations'
        }
      })
      customToast.success('Turnstile SiteKey saved')
    } catch (error) {
      customToast.error('Failed to save Turnstile SiteKey')
      console.error(error)
    }
  }

  const handleTurnstileSecretKeySave = async () => {
    try {
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ConversationsSettings:Tenants:${selectedTenant.tenantName}:Turnstile:SecretKey`,
          value: turnstileSecretKey,
          label: 'conversations'
        }
      })
      customToast.success('Turnstile SecretKey saved')
    } catch (error) {
      customToast.error('Failed to save Turnstile SecretKey')
      console.error(error)
    }
  }

  const handleSmtpSenderNameSave = async () => {
    try {
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ConversationsSettings:Tenants:${selectedTenant.tenantName}:SmtpSettings:DefaultSender`,
          value: smtpSenderName,
          label: 'conversations',
          requireExisting: true
        }
      })
      customToast.success('SMTP Sender Name saved')
    } catch (error) {
      customToast.error(saveErrorMessage(error, 'Failed to save SMTP Sender Name'))
      console.error(error)
    }
  }

  const handleRssSettingsSave = async () => {
    const entries = normalizeIpList(rssIpWhitelist)
    const invalidEntries = entries.filter((entry) => !isValidIpAddress(entry))
    if (invalidEntries.length > 0) {
      customToast.warning(`Invalid IP address entries will be ignored by the provider: ${invalidEntries.join(', ')}`)
    }

    // Persist the normalized list that was just validated (drops empty segments / inconsistent
    // spacing) so what is stored matches what the operator was shown.
    const normalizedWhitelist = entries.join(', ')
    if (rssRedirectBaseUrl.length === 0) {
      customToast.warning('This tenant has no Suite hostname, so the feed items will carry no link.')
    }

    try {
      // Write the whitelist before the enabled flag: if the second write fails, the feed is left
      // disabled (safe) rather than enabled with a whitelist that never persisted.
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ContentProviderSettings:Tenants:${selectedTenant.tenantName}:RssSettings:IpWhitelistCsv`,
          value: normalizedWhitelist,
          label: 'cmscontentprovider',
          requireExisting: true
        }
      })
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ContentProviderSettings:Tenants:${selectedTenant.tenantName}:RssSettings:RedirectBaseUrl`,
          value: rssRedirectBaseUrl,
          label: 'cmscontentprovider'
        }
      })
      await fetchApi({
        endpoint: `/api/appconfiguration/${selectedTenantId}`,
        method: 'POST',
        body: {
          key: `ContentProviderSettings:Tenants:${selectedTenant.tenantName}:RssSettings:Enabled`,
          value: rssEnabled ? 'true' : 'false',
          label: 'cmscontentprovider',
          requireExisting: true
        }
      })
      setRssIpWhitelist(normalizedWhitelist)
      setSavedRssIpWhitelist(normalizedWhitelist)
      setSavedRssEnabled(rssEnabled)
      customToast.success('RSS settings saved')
    } catch (error) {
      customToast.error(saveErrorMessage(error, 'Failed to save RSS settings'))
      console.error(error)
    }
  }

  const notProvisionedNote = (
    <p
      style={{
        width: '100%',
        textAlign: 'left',
        fontFamily: APP_FONT,
        fontSize: '12px',
        color: 'var(--tma-warning-text)',
        margin: '8px 0 0 0'
      }}
    >
      {NOT_PROVISIONED_NOTE}
    </p>
  )

  const renderConversationSettings = () => {
    return (
      <Container isVertical align='center' gap={0} spacing={0} isFullWidth style={sectionContainerStyle}>
        <Header style={headerStyle}>Conversations settings</Header>
        <Toast />
        <Container align='center' gap={0} spacing={0} isFullWidth style={innerContainerStyle}>
          <Autocomplete
            style={{ width: '100%' }}
            isSearchable={false}
            isMulti
            options={conversationPackOptions}
            value={selectedConversationPacks}
            onChange={setSelectedConversationPacks}
            placeholder='Select conversation pack'
          />
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            leftElement={<Icon iconName='OfficeFileDoc1' size={16} />}
            onClick={handleConversationPackChoice}
            isDisabled={packsProvisioned !== true}
            text={`Save`}
            variant='contained'
            style={{ marginLeft: '10px', float: 'right' }}
          ></Button2>
        </Container>
        {packsProvisioned === false && notProvisionedNote}
        <Container align='center' gap={0} spacing={0} isFullWidth style={{ ...innerContainerStyle, marginTop: '15px' }}>
          <TextFieldWrapper labelText='Turnstile SiteKey' style={{ flex: 1 }}>
            <TextField
              value={turnstileSiteKey}
              onChange={(e) => setTurnstileSiteKey(e.target.value)}
              type='text'
              variant='default'
              placeholder='Enter Turnstile SiteKey'
            />
          </TextFieldWrapper>
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            leftElement={<Icon iconName='OfficeFileDoc1' size={16} />}
            onClick={handleTurnstileSiteKeySave}
            text={`Save`}
            variant='contained'
            style={{ marginLeft: '10px', marginTop: '20px' }}
          ></Button2>
        </Container>
        <Container align='center' gap={0} spacing={0} isFullWidth style={{ ...innerContainerStyle, marginTop: '15px' }}>
          <TextFieldWrapper labelText='Turnstile SecretKey' style={{ flex: 1 }}>
            <TextField
              value={turnstileSecretKey}
              onChange={(e) => setTurnstileSecretKey(e.target.value)}
              type='text'
              variant='default'
              placeholder='Enter Turnstile SecretKey'
            />
          </TextFieldWrapper>
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            leftElement={<Icon iconName='OfficeFileDoc1' size={16} />}
            onClick={handleTurnstileSecretKeySave}
            text={`Save`}
            variant='contained'
            style={{ marginLeft: '10px', marginTop: '20px' }}
          ></Button2>
        </Container>
        <Container align='center' gap={0} spacing={0} isFullWidth style={{ ...innerContainerStyle, marginTop: '15px' }}>
          <TextFieldWrapper labelText='SMTP Sender Name' style={{ flex: 1 }}>
            <TextField
              value={smtpSenderName}
              onChange={(e) => setSmtpSenderName(e.target.value)}
              type='text'
              variant='default'
              placeholder='Enter SMTP Sender Name'
            />
          </TextFieldWrapper>
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            leftElement={<Icon iconName='OfficeFileDoc1' size={16} />}
            onClick={handleSmtpSenderNameSave}
            isDisabled={smtpProvisioned !== true}
            text={`Save`}
            variant='contained'
            style={{ marginLeft: '10px', marginTop: '20px' }}
          ></Button2>
        </Container>
        {smtpProvisioned === false && notProvisionedNote}
      </Container>
    )
  }

  const renderMediaProviderSettings = () => {
    return (
      <Container isVertical align='center' gap={0} spacing={0} isFullWidth style={sectionContainerStyle}>
        <Header style={headerStyle}>Media Provider Settings</Header>
        <Container align='center' gap={0} spacing={0} isFullWidth style={innerContainerStyle}></Container>
      </Container>
    )
  }

  const renderContentProviderSettings = () => {
    const ips = normalizeIpList(rssIpWhitelist)
    const ipCount = ips.length
    const savedIps = normalizeIpList(savedRssIpWhitelist)
    const enabledPending = rssEnabled !== savedRssEnabled
    const whitelistPending = ips.join(', ') !== savedIps.join(', ')
    const hasPendingChanges = enabledPending || whitelistPending
    const rssProvisioned = rssEnabledProvisioned === true && rssWhitelistProvisioned === true
    const rssNotProvisioned = rssEnabledProvisioned === false || rssWhitelistProvisioned === false
    const pendingSummary = [enabledPending ? 'the feed switch' : null, whitelistPending ? 'the IP whitelist' : null]
      .filter(Boolean)
      .join(' and ')
    // Describes what the tenant is doing right now, so it stays put while the switch is flipped.
    const liveStatusText = savedRssEnabled
      ? savedIps.length > 0
        ? `Serving to ${savedIps.length} whitelisted address${savedIps.length === 1 ? '' : 'es'}.`
        : 'On, but no IPs are whitelisted, so nothing is served yet.'
      : 'The feed is off. No requests are served.'
    const feedUrl = selectedTenant
      ? `https://cmscontentprovider.${selectedTenant.environment}.embracecloud.${embracecloudTld(selectedTenant.environment)}/api/v1/rss/feed?tenant=${selectedTenant.tenantName}`
      : ''

    // Small tinted status chip; the same colours drive the on/off pill and the reach note.
    const chip = (color: string, bg: string): React.CSSProperties => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontFamily: APP_FONT,
      fontSize: '12px',
      fontWeight: 600,
      color,
      background: bg,
      borderRadius: '999px',
      padding: '3px 10px'
    })

    const copyFeedUrl = () => {
      if (navigator.clipboard && feedUrl) {
        navigator.clipboard.writeText(feedUrl).then(
          () => customToast.success('Feed URL copied'),
          () => customToast.error('Could not copy the URL')
        )
      }
    }

    return (
      <Container isVertical align='center' gap={0} spacing={0} isFullWidth style={sectionContainerStyle}>
        <Header style={headerStyle}>Content Provider Settings</Header>

        {/* RSS on/off with a live status pill and a one-line consequence. */}
        <div
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', textAlign: 'left' }}
        >
          <div>
            <div style={{ fontFamily: APP_FONT, fontSize: '14px', fontWeight: 600, color: 'var(--tma-text-strong)' }}>
              Serve the RSS feed
            </div>
            <div style={{ fontFamily: APP_FONT, fontSize: '12px', color: 'var(--tma-text-muted)', marginTop: '2px' }}>{liveStatusText}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {/* The pill follows what is stored, never the switch, so it cannot claim the feed is on early. */}
            <span
              style={
                enabledPending
                  ? chip('var(--tma-warning-text)', 'var(--tma-warning-tint)')
                  : savedRssEnabled
                    ? chip('var(--tma-success-text)', 'var(--tma-success-tint)')
                    : chip('var(--tma-text-muted)', 'var(--tma-active-bg)')
              }
            >
              {enabledPending && <Icon iconName='AlertTriangle' size={14} color='var(--tma-warning-text)' />}
              {enabledPending
                ? `${savedRssEnabled ? 'On' : 'Off'} until you save, then ${rssEnabled ? 'On' : 'Off'}`
                : savedRssEnabled
                  ? 'On'
                  : 'Off'}
            </span>
            <span
              style={{
                display: 'inline-flex',
                padding: '3px',
                borderRadius: '999px',
                border: `1px dashed ${enabledPending ? 'var(--tma-warning-text)' : 'transparent'}`
              }}
            >
              <Switch
                name='rssEnabled'
                aria-label='Serve the RSS feed'
                value='rssEnabled'
                onClick={() => setRssEnabled(!rssEnabled)}
                isChecked={rssEnabled}
              />
            </span>
          </div>
        </div>

        <Container align='center' gap={0} spacing={0} isFullWidth style={{ ...innerContainerStyle, marginTop: '18px' }}>
          <TextFieldWrapper labelText='RSS Redirect Base URL' style={{ flex: 1 }}>
            <TextField value={rssRedirectBaseUrl} isDisabled type='text' variant='default' />
          </TextFieldWrapper>
        </Container>

        <p
          style={{
            width: '100%',
            textAlign: 'left',
            fontFamily: APP_FONT,
            fontSize: '12px',
            color: 'var(--tma-text-muted)',
            margin: '8px 0 0 0'
          }}
        >
          The Suite URL each feed item links back to, derived from the tenant Suite hostname. Save to store it; without it the provider
          leaves the item links out of the feed.
        </p>

        {/* IP whitelist */}
        <Container align='center' gap={0} spacing={0} isFullWidth style={{ ...innerContainerStyle, marginTop: '18px' }}>
          <TextFieldWrapper labelText='RSS IP Whitelist' style={{ flex: 1 }}>
            <TextField
              value={rssIpWhitelist}
              onChange={(e) => setRssIpWhitelist(e.target.value)}
              type='text'
              variant='default'
              placeholder='e.g. 1.2.3.4, 5.6.7.8, ::1'
            />
          </TextFieldWrapper>
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            leftElement={<Icon iconName='OfficeFileDoc1' size={16} />}
            onClick={handleRssSettingsSave}
            isDisabled={!rssProvisioned}
            text={hasPendingChanges ? 'Save changes' : 'Save'}
            variant='contained'
            style={{ marginLeft: '10px', marginTop: '20px' }}
          ></Button2>
        </Container>
        {rssNotProvisioned && notProvisionedNote}

        <p
          style={{
            width: '100%',
            textAlign: 'left',
            fontFamily: APP_FONT,
            fontSize: '12px',
            color: 'var(--tma-text-muted)',
            margin: '8px 0 0 0'
          }}
        >
          Comma-separated individual IPv4/IPv6 addresses (not CIDR). An IP must be whitelisted to receive the feed.
        </p>

        {/* The switch sits well above this button, so the pending write is spelled out next to it too. */}
        {hasPendingChanges && (
          <div style={{ width: '100%', marginTop: '10px', textAlign: 'left' }}>
            <span style={chip('var(--tma-warning-text)', 'var(--tma-warning-tint)')}>
              <Icon iconName='AlertTriangle' size={14} color='var(--tma-warning-text)' />
              {`Not saved yet: ${pendingSummary}. Nothing changes for the tenant until you press Save.`}
            </span>
          </div>
        )}

        {/* Whether the feed currently reaches anyone. */}
        <div style={{ width: '100%', marginTop: '10px', textAlign: 'left' }}>
          {ipCount === 0 ? (
            <span style={chip('var(--tma-warning-text)', 'var(--tma-warning-tint)')}>
              <Icon iconName='AlertTriangle' size={14} color='var(--tma-warning-text)' />
              {whitelistPending
                ? `Saving clears the whitelist, and the ${savedIps.length} stored address${
                    savedIps.length === 1 ? '' : 'es'
                  } can still reach the feed until then.`
                : 'No IPs whitelisted, so the feed reaches no one even when on.'}
            </span>
          ) : (
            <span
              style={
                whitelistPending
                  ? chip('var(--tma-warning-text)', 'var(--tma-warning-tint)')
                  : chip('var(--tma-success-text)', 'var(--tma-success-tint)')
              }
            >
              <Icon
                iconName={whitelistPending ? 'AlertTriangle' : 'Check1'}
                size={14}
                color={whitelistPending ? 'var(--tma-warning-text)' : 'var(--tma-success-text)'}
              />
              {whitelistPending
                ? `${ipCount} address${ipCount === 1 ? '' : 'es'} may reach the feed once you save.`
                : `${ipCount} address${ipCount === 1 ? '' : 'es'} may reach the feed.`}
            </span>
          )}
        </div>

        {/* Feed URL as a GET endpoint. */}
        {selectedTenant && (
          <div style={{ width: '100%', marginTop: '18px', textAlign: 'left' }}>
            <div
              style={{
                fontFamily: APP_FONT,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--tma-text-muted)',
                marginBottom: '8px'
              }}
            >
              Feed URL
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'var(--tma-card-bg)',
                border: '1px solid var(--tma-list-card-border)',
                borderRadius: '8px',
                padding: '8px 8px 8px 10px'
              }}
            >
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'var(--tma-success-text)',
                  background: 'var(--tma-success-tint)',
                  borderRadius: '6px',
                  padding: '3px 7px',
                  flexShrink: 0
                }}
              >
                GET
              </span>
              <span
                className='custom-scrollbar'
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: 'var(--tma-text-strong)',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  padding: '2px 0'
                }}
              >
                {feedUrl}
              </span>
              <Button2
                buttonRadius='6px'
                buttonSize='30px'
                variant='outlined'
                leftElement={<Icon iconName='CopyPaste' size={14} />}
                text='Copy'
                type='button'
                onClick={copyFeedUrl}
                style={{ flexShrink: 0 }}
              />
            </div>
          </div>
        )}
      </Container>
    )
  }

  const renderPortalProviderSettings = () => {
    return (
      <Container isVertical align='center' gap={0} spacing={0} isFullWidth style={sectionContainerStyle}>
        <Header style={headerStyle}>Portal Provider Settings</Header>
        <Container align='center' gap={0} spacing={0} isFullWidth style={innerContainerStyle}></Container>
        <Divider />
      </Container>
    )
  }

  return (
    <>
      {renderConversationSettings()}
      {renderContentProviderSettings()}
    </>
  )
}

export default CMSAccordionContent
